import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { groupAsksItsOwn, proposalWhat, type GroupDecision, type GroupView, type PlanInWords, type SetupResponse } from "@joogoos/contracts";
import { api } from "../api.js";
import { Companies } from "./Companies.js";
import { CompanyPanel } from "./CompanyPanel.js";
import { Star } from "./GroupList.js";
import { KINDS, yesCosts, TODAY, Waiting, when } from "./Today.js";
import { Card } from "./setup/chrome.js";
import { GroupForm } from "./setup/Groups.js";
import { GroupLetters } from "./Letters.js";
import { Flag, flagOf, placesOf, SETUP, STATE_TONE, stateOf, useSetup } from "./groups.js";

/**
 * **One group** (38, ADR 0068): what a person opens, with four tabs.
 *
 * 1. **Overview** — what Joogoos is doing in it, in four lines from its numbers (38 step 4); what needs the owner,
 *    its proposals among them, each with its price; and its numbers.
 * 2. **Companies** — only this group's.
 * 3. **Letters** — the group's letters, read top to bottom, each with a rule saying who gets it and each part
 *    falling back to the usual letter in Settings (38 step 2, `GroupLetters`).
 * 4. **Limits** — who and where, the group's own week, who sends and when it stops itself, each empty for the
 *    workspace's; and what Joogoos decides, each with *Set it myself* (38 step 3).
 */

export type GroupTab = "overview" | "companies" | "letters" | "limits";
const TABS: ReadonlyArray<[GroupTab, string]> = [["overview", "Overview"], ["companies", "Companies"], ["letters", "Letters"], ["limits", "Limits"]];

export function GroupPage({ id, tab, onTab, onGone }: { id: string; tab: GroupTab; onTab: (t: GroupTab) => void; onGone: () => void }) {
  const qc = useQueryClient();
  const q = useSetup();
  const today = useQuery({ queryKey: TODAY, queryFn: () => api.today() });
  const keep = (d: SetupResponse) => qc.setQueryData(SETUP, d);
  const mark = useMutation({ mutationFn: (v: { starred?: boolean; paused?: boolean }) => api.markGroups({ ids: [id], ...v }), onSuccess: keep });
  const retire = useMutation({ mutationFn: () => api.retireGroup(id, "retired by the owner"), onSuccess: keep });
  const [retiring, setRetiring] = useState(false);

  const d = q.data;
  if (q.isPending) return <p className="text-sm text-faint">…</p>;
  if (!d) return <p className="text-sm text-bad">{String(q.error ?? "That did not load.")}</p>;
  const g = d.groups.find((x) => x.id === id);
  if (!g) {
    return (
      <p className="text-[13px] text-muted">
        That group is not here any more. <button onClick={onGone} className="text-coral font-medium">Every group</button>
      </p>
    );
  }
  const state = stateOf(g, today.data?.pauses ?? []);
  const retired = g.state === "dropped";

  return (
    <div className="flex flex-col gap-4 max-w-[1100px]">
      <div className="flex flex-wrap items-center gap-2.5">
        <Flag code={flagOf(g)} />
        <h1 className="text-base font-semibold">{g.name}</h1>
        <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded font-semibold ${STATE_TONE[state]}`}>{state}</span>
        <Star on={g.starred} name={g.name} disabled={!d.mayEdit || mark.isPending || retired} onClick={() => mark.mutate({ starred: !g.starred })} />
        <span className="text-[12px] text-muted">{placesOf(g) || "no place yet"}{g.folder ? ` · ${g.folder}` : ""}</span>
        {g.grownAt ? <span className="px-1.5 py-0.5 rounded bg-line-2 text-ink-2 text-[10px] font-semibold uppercase tracking-[.05em]">grown by Joogoos</span> : null}
        {d.mayEdit && !retired ? (
          <span className="ml-auto flex items-center gap-1.5">
            <button onClick={() => mark.mutate({ paused: !g.paused })} disabled={mark.isPending}
              className="px-2.5 py-1 rounded-lg border border-line-2 bg-page text-[11.5px] font-medium disabled:opacity-50"
            >{g.paused ? "Start it again" : "Pause"}</button>
            {retiring ? (
              <>
                <span className="text-[11.5px] text-muted">Its companies stay, under its name.</span>
                <button onClick={() => retire.mutate()} disabled={retire.isPending} className="px-2.5 py-1 rounded-lg bg-ink text-white text-[11.5px] font-semibold">Retire it</button>
                <button onClick={() => setRetiring(false)} className="px-2.5 py-1 rounded-lg border border-line-2 bg-page text-[11.5px] font-medium">Keep it</button>
              </>
            ) : (
              <button onClick={() => setRetiring(true)} className="px-2.5 py-1 rounded-lg border border-line-2 bg-page text-[11.5px] font-medium">Retire</button>
            )}
          </span>
        ) : null}
      </div>
      {g.paused ? (
        <p className="text-[12px] text-muted">Paused: Joogoos does not look for, read or write to companies in this group until you start it again.</p>
      ) : null}
      {mark.error || retire.error ? <p className="text-xs text-bad">{String(mark.error ?? retire.error)}</p> : null}

      <div role="tablist" aria-label={g.name} className="flex gap-0.5 border-b border-line">
        {TABS.map(([key, label]) => (
          <button key={key} role="tab" aria-selected={tab === key} onClick={() => onTab(key)}
            className={`px-3.5 py-1.5 text-[13px] border-b-2 -mb-px ${tab === key ? "border-coral text-ink font-semibold" : "border-transparent text-muted font-medium"}`}
          >{label}</button>
        ))}
      </div>

      {tab === "overview" ? <Overview g={g} plan={d.plans[g.id]} />
        : tab === "companies" ? <Companies groupId={g.id} />
          : tab === "letters" ? <GroupLetters key={g.id} g={g} d={d} keep={keep} />
            : <Limits key={g.id} g={g} d={d} keep={keep} />}
    </div>
  );
}

/* --------------------------------------------------------------------------------------------- Overview ---- */

function Overview({ g, plan }: { g: GroupView; plan: PlanInWords | undefined }) {
  const qc = useQueryClient();
  const decide = useMutation({
    mutationFn: ({ id, decision }: { id: number; decision: "apply" | "reject" }) => api.decideProposal(id, decision),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: TODAY }); void qc.invalidateQueries({ queryKey: SETUP }); },
  });
  const today = useQuery({ queryKey: TODAY, queryFn: () => api.today() });
  const resume = useMutation({ mutationFn: (pause: number) => api.resumeCell(pause), onSuccess: () => qc.invalidateQueries({ queryKey: TODAY }) });
  const [open, setOpen] = useState<string | null>(null);
  const t = today.data;
  const replies = t?.replies.filter((r) => r.groupId === g.id) ?? [];
  const letters = t?.toApprove.filter((l) => l.groupId === g.id) ?? [];
  const stopped = t?.pauses.filter((p) => p.groupId === g.id) ?? [];
  const proposals = t?.proposals.filter((p) => p.groupId === g.id) ?? [];
  const owed = replies.length + letters.length + stopped.length + proposals.length;
  // A group reached through another business says its own ask or writes nothing; said here, where it is opened.
  const noAsk = groupAsksItsOwn(g.route) && g.letters.some((l) => !l.ask.trim());

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_300px] items-start">
      <div className="flex flex-col gap-4 min-w-0">
        {plan ? (
          <section className="rounded-xl border border-line bg-panel p-3.5 flex flex-col gap-1.5" aria-label="What Joogoos is doing">
            <h2 className="text-[13px] font-semibold">What Joogoos is doing</h2>
            {([["Doing", plan.doing], ["Next", plan.next], ["Then", plan.then], ["Learned", plan.learned]] as const).map(([label, line]) => (
              <p key={label} className="text-[12.5px] leading-relaxed"><span className="inline-block w-[64px] text-[11px] uppercase tracking-[.06em] font-semibold text-muted">{label}</span>{line}</p>
            ))}
          </section>
        ) : null}
        <section className="flex flex-col gap-2">
          <div className="flex items-center gap-2.5">
            <h2 className="text-[13px] font-semibold">Needs you</h2>
            {owed ? <span className="px-2 py-0.5 rounded-full bg-accent/10 text-accent text-[11px] font-semibold tabular-nums">{owed}</span> : null}
          </div>
          {noAsk ? <p className="text-[12.5px] text-coral">{g.letters.length > 1 ? "A letter here has no ask yet, so its companies are not written to." : "No ask yet, so no letter is written for this group."} Letters is where it goes.</p> : null}
          {!t ? <p className="text-[12.5px] text-faint">…</p>
            : !owed ? <p className="text-[12.5px] text-faint">Nothing in this group waits for you.</p>
              : (
                <div className="flex flex-col gap-2.5">
                  {replies.map((r) => (
                    <div key={r.prospectId} className="rounded-xl border border-accent/30 bg-panel px-3.5 py-2.5 flex flex-wrap items-center gap-2">
                      <span className="px-1.5 py-0.5 rounded bg-accent/10 text-accent text-[10px] font-semibold uppercase tracking-[.05em]">replied</span>
                      <span className="text-[13px] font-semibold">{r.companyName}</span>
                      <span className="text-[11.5px] text-muted">{when(r.at)} · answer it in your mailbox, then say how it went on Today</span>
                    </div>
                  ))}
                  {letters.length ? (
                    <div className="rounded-xl border border-line bg-panel overflow-hidden">
                      {letters.map((l) => <Waiting key={l.letterId} letter={l} onOpen={() => setOpen(l.prospectId)} />)}
                    </div>
                  ) : null}
                  {proposals.map((p) => (
                    <div key={p.id} className="rounded-xl border border-line bg-panel px-3.5 py-2.5 flex flex-col gap-1.5">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="px-1.5 py-0.5 rounded bg-line-2 text-ink-2 text-[10px] font-semibold uppercase tracking-[.05em]">{KINDS[p.kind]}</span>
                        <span className="text-[12.5px]">{proposalWhat(p)}</span>
                      </span>
                      <span className="text-[12px] text-muted">{p.why} {yesCosts(p)}</span>
                      <span className="flex gap-1.5">
                        <button onClick={() => decide.mutate({ id: p.id, decision: "apply" })} disabled={!t?.mayEdit || decide.isPending}
                          className="px-2.5 py-1 rounded-lg bg-ink text-white text-[11.5px] font-semibold disabled:opacity-50">Yes</button>
                        <button onClick={() => decide.mutate({ id: p.id, decision: "reject" })} disabled={!t?.mayEdit || decide.isPending}
                          className="px-2.5 py-1 rounded-lg border border-line-2 bg-page text-[11.5px] font-medium disabled:opacity-50">No</button>
                      </span>
                      {decide.error && decide.variables?.id === p.id ? <span className="text-xs text-bad">{String(decide.error)}</span> : null}
                    </div>
                  ))}
                  {stopped.map((p) => (
                    <div key={p.id} className="rounded-xl border border-accent/30 bg-panel px-3.5 py-2.5 flex flex-col gap-1.5">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="px-1.5 py-0.5 rounded bg-accent/10 text-accent text-[10px] font-semibold uppercase tracking-[.05em]">stopped itself</span>
                        <span className="text-[13px] font-semibold">{p.country}</span>
                      </span>
                      <span className="text-[12px] text-muted">{p.reason}. The rest of the group goes on.</span>
                      <button onClick={() => resume.mutate(p.id)} disabled={!t.mayEdit || resume.isPending}
                        className="self-start px-2.5 py-1 rounded-lg border border-line-2 bg-page text-[11.5px] font-medium disabled:opacity-50">Start it again</button>
                    </div>
                  ))}
                </div>
              )}
        </section>
        <section className="flex flex-col gap-1">
          <h2 className="text-[13px] font-semibold">Who it looks for</h2>
          <p className="text-[12.5px] leading-relaxed">{g.looksFor}</p>
          {g.why ? <p className="text-[12px] text-muted leading-relaxed">{g.why}</p> : null}
        </section>
      </div>
      <section className="rounded-xl border border-line bg-panel p-3.5 flex flex-col gap-3" aria-label="This group so far">
        <h2 className="text-[11px] uppercase tracking-[.08em] font-semibold text-muted">This group so far</h2>
        <div className="grid grid-cols-2 gap-3">
          {([["found", g.found], ["written to", g.written], ["answered", g.answered], ["calls booked", g.meetings]] as const).map(([label, n]) => (
            <span key={label} className="flex flex-col">
              <b className="text-[17px] tabular-nums">{n}</b>
              <span className="text-[11px] text-muted">{label}</span>
            </span>
          ))}
          <span className="flex flex-col">
            <b className="text-[17px] tabular-nums">${g.spentThisMonth}</b>
            <span className="text-[11px] text-muted">this month</span>
          </span>
          <span className="flex flex-col">
            <b className="text-[17px] tabular-nums">{g.go}</b>
            <span className="text-[11px] text-muted">worth a letter</span>
          </span>
        </div>
        <p className="text-[11px] text-faint leading-relaxed">This month is reading and writing to its companies. Searches count in the total on Today.</p>
      </section>
      {open && <CompanyPanel prospectId={open} onClose={() => setOpen(null)} onNext={(next) => setOpen(next)} />}
    </div>
  );
}

/* ----------------------------------------------------------------------------------------------- Limits ---- */

function Limits({ g, d, keep }: { g: GroupView; d: SetupResponse; keep: (d: SetupResponse) => void }) {
  const qc = useQueryClient();
  const loop = useQuery({ queryKey: ["customers", "autopilot"], queryFn: () => api.autopilot() });
  const save = useMutation({
    mutationFn: (group: unknown) => api.saveGroup(group, g.id),
    onSuccess: (r) => { keep(r); setEditing(false); void qc.invalidateQueries({ queryKey: TODAY }); },
  });
  const [editing, setEditing] = useState(false);
  const s = loop.data?.switches;
  const retired = g.state === "dropped";

  return (
    <div className="flex flex-col gap-3.5 max-w-[790px]">
      <Card title="Who and where" aside={<>
        <span className="text-xs text-muted">you set these</span>
        {d.mayEdit && !retired && !editing ? <button onClick={() => setEditing(true)} className="ml-auto text-xs font-medium text-coral">Change</button> : null}
      </>}>
        {editing ? (
          <GroupForm
            group={g} markets={d.markets} business={d.settings} answersSeen={d.answersSeen} letter={false}
            onCancel={() => setEditing(false)} onSave={(group) => save.mutate(group)} busy={save.isPending} error={save.error}
          />
        ) : (
          <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1.5 text-[12.5px]">
            <dt className="text-muted">Looks for</dt><dd>{g.looksFor}</dd>
            <dt className="text-muted">Who they are</dt>
            <dd>{g.audience === "public" ? "the public" : "a business"} · {g.route === "direct" ? "written to" : g.route === "through_business" ? "through the businesses that hold them" : "a list you bring"}</dd>
            <dt className="text-muted">Where</dt><dd>{placesOf(g) || <span className="text-coral">no place yet, so nothing is looked for</span>}</dd>
          </dl>
        )}
      </Card>

      <OwnLimits g={g} d={d} keep={keep} s={s} />
    </div>
  );
}

/** The workspace's switches, as the Limits tab reads them. */
type Switches = NonNullable<Awaited<ReturnType<typeof api.autopilot>>["switches"]>;

/** What Joogoos decides inside a group, in words, each with *Set it myself* (38 step 3). */
const DECISIONS: ReadonlyArray<[GroupDecision, string, string]> = [
  ["places", "Where to look next", "it tries a new place for this group when the numbers say so"],
  ["words", "What it looks for", "it rewords what this group looks for when the answers point elsewhere"],
  ["narrower", "Narrower groups", "it proposes a narrower group inside this one when part of it answers better"],
  ["versions", "Calling a test", "it keeps the version of a letter that is clearly ahead, once one is"],
];

/**
 * What the owner sets for this group alone (38 step 3, ADR 0068 point 4): the week's letters and money, who sends,
 * and when a place stops itself — each empty for the workspace's, shared, and never more than the workspace's total —
 * and beside them what Joogoos decides, each with *Set it myself*.
 */
function OwnLimits({ g, d, keep, s }: { g: GroupView; d: SetupResponse; keep: (d: SetupResponse) => void; s: Switches | undefined }) {
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (patch: Record<string, unknown>) => api.saveGroupLimits(g.id, patch),
    onSuccess: (r) => { keep(r); void qc.invalidateQueries({ queryKey: TODAY }); },
  });
  const l = g.limits;
  const may = d.mayEdit && g.state !== "dropped" && !save.isPending;
  const int = (v: string) => (v.trim() === "" ? null : Math.max(0, Math.trunc(Number(v))));
  const tenths = (v: string) => (v.trim() === "" ? null : Math.max(1, Math.round(Number(v) * 10)));
  const over = (mine: number | null, all: number | undefined) => mine !== null && all !== undefined && mine > all;
  const overMoney = l.weeklyBudget !== null && s !== undefined && Number(l.weeklyBudget) > Number(s.weeklyBudget);

  return (
    <>
      <Card title="Each week" aside={<span className="text-xs text-muted">you set these · empty is the shared total</span>}>
        <div className="flex flex-wrap items-center gap-3 text-[12.5px]">
          <label className="flex items-center gap-2">
            <span className="text-muted">At most</span>
            <input aria-label="Letters a week for this group" inputMode="numeric" disabled={!may} defaultValue={l.lettersPerWeek ?? ""}
              placeholder={s ? String(s.lettersPerWeek) : ""}
              onBlur={(e) => int(e.target.value) !== l.lettersPerWeek && save.mutate({ lettersPerWeek: int(e.target.value) })}
              className="w-20 px-2.5 py-1 rounded-lg border border-line-2 bg-panel" />
            <span className="text-muted">letters and</span>
          </label>
          <label className="flex items-center gap-2">
            <span className="text-muted">$</span>
            <input aria-label="Money a week for this group" inputMode="decimal" disabled={!may} defaultValue={l.weeklyBudget ?? ""}
              placeholder={s ? s.weeklyBudget : ""}
              onBlur={(e) => (e.target.value.trim() || null) !== l.weeklyBudget && save.mutate({ weeklyBudget: e.target.value.trim() || null })}
              className="w-24 px-2.5 py-1 rounded-lg border border-line-2 bg-panel" />
            <span className="text-muted">a week</span>
          </label>
        </div>
        <p className="text-[11.5px] text-faint leading-relaxed">
          {s ? <>Every group together stays inside <b>{s.lettersPerWeek} letters</b> and <b>${s.weeklyBudget}</b> a week, in Settings. </> : null}
          {over(l.lettersPerWeek, s?.lettersPerWeek) || overMoney
            ? "This group's number is more than the total, so the total is what holds."
            : "What this group does not use is not handed to another."}
        </p>
      </Card>

      <Card title="Who sends" aside={<span className="text-xs text-muted">you set this</span>}>
        <div className="flex flex-col gap-1.5 text-[12.5px]" role="radiogroup" aria-label="Who sends">
          <label className="flex items-center gap-2">
            <input type="radio" name={`sends-${g.id}`} checked={!l.personSends} disabled={!may} onChange={() => save.mutate({ personSends: false })} />
            <span>{!s ? "…" : s.sends ? `Joogoos may send the letters it wrote, up to ${s.lettersPerDay} a day across every group, while this box rehearses` : "as Settings says: today you press Send on every letter"}</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" name={`sends-${g.id}`} checked={l.personSends} disabled={!may} onChange={() => save.mutate({ personSends: true })} />
            <span>You press Send on every letter of this group</span>
          </label>
        </div>
        {!l.personSends ? (
          <label className="flex flex-wrap items-center gap-2 text-[12.5px]">
            <span className="text-muted">When Send is on, Joogoos sends at most</span>
            <input aria-label="Letters a day Joogoos sends for this group" inputMode="numeric" disabled={!may} defaultValue={l.lettersPerDay}
              onBlur={(e) => { const n = Math.max(0, Math.min(20, Math.trunc(Number(e.target.value) || 0))); if (n !== l.lettersPerDay) save.mutate({ lettersPerDay: n }); }}
              className="w-16 px-2.5 py-1 rounded-lg border border-line-2 bg-panel" />
            <span className="text-muted">of this group's letters a day{g.sentAlone ? ` · ${g.sentAlone} sent by Joogoos so far` : ""}</span>
          </label>
        ) : null}
      </Card>

      <Card title="Stops by itself when" aside={<span className="text-xs text-muted">and tells you on Today</span>}>
        <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
          <span className="text-muted">its bounces pass</span>
          <input aria-label="Bounces line for this group" inputMode="decimal" disabled={!may} defaultValue={l.pauseBounceTenths === null ? "" : l.pauseBounceTenths / 10}
            placeholder={s ? String(s.pauseBounceTenths / 10) : ""}
            onBlur={(e) => tenths(e.target.value) !== l.pauseBounceTenths && save.mutate({ pauseBounceTenths: tenths(e.target.value) })}
            className="w-16 px-2.5 py-1 rounded-lg border border-line-2 bg-panel" />
          <span className="text-muted">% or its asks to stop pass</span>
          <input aria-label="Asks to stop line for this group" inputMode="decimal" disabled={!may} defaultValue={l.pauseOptoutTenths === null ? "" : l.pauseOptoutTenths / 10}
            placeholder={s ? String(s.pauseOptoutTenths / 10) : ""}
            onBlur={(e) => tenths(e.target.value) !== l.pauseOptoutTenths && save.mutate({ pauseOptoutTenths: tenths(e.target.value) })}
            className="w-16 px-2.5 py-1 rounded-lg border border-line-2 bg-panel" />
          <span className="text-muted">%</span>
        </div>
        <p className="text-[11.5px] text-faint">Only that place stops; the rest of the group goes on. Empty is the line in Settings.</p>
      </Card>

      <Card title="What Joogoos decides" aside={<span className="text-xs text-muted">and shows you on Today</span>}>
        <ul className="flex flex-col gap-2">
          {DECISIONS.map(([key, title, does]) => {
            const mine = l.ownerDecides.includes(key);
            return (
              <li key={key} className="flex flex-wrap items-center gap-2 text-[12.5px]">
                <b className="font-semibold">{title}</b>
                <span className="text-muted">{mine ? "you decide: every proposal of this kind waits for your yes" : does}</span>
                {d.mayEdit && g.state !== "dropped" ? (
                  <button disabled={save.isPending} className="ml-auto text-xs font-medium text-coral"
                    onClick={() => save.mutate({ ownerDecides: mine ? l.ownerDecides.filter((x) => x !== key) : [...l.ownerDecides, key] })}
                  >{mine ? "Let Joogoos decide" : "Set it myself"}</button>
                ) : null}
              </li>
            );
          })}
        </ul>
        <p className="text-[11.5px] text-faint leading-relaxed">
          How far Joogoos goes on its own is the dial in Settings; what you set yourself here waits for you whatever it says.
          Retiring a group always waits for you.
        </p>
      </Card>
      {save.error ? <p className="text-xs text-bad">{String(save.error)}</p> : null}
    </>
  );
}
